import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getMe, logout as apiLogout, deleteAccount as apiDeleteAccount, forgetLocalAccountState, claimBrowserForUser, setUnauthorizedHandler, setSubscriptionRequiredHandler } from '../lib/authApi';
import { resetLibraryIndex } from '../lib/libraryIndex';

const AuthContext = createContext(null);

/**
 * Who is signed in. `user` is null while signed out, `loading` is true until the first /auth/me answer.
 * The server is the source of truth (httpOnly cookie) — the frontend never reads the cookie itself.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expiredNotice, setExpiredNotice] = useState(false); // false | true (session ended) | 'deleted' (account removed)

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      // If a different account used this browser last (signed in from another tab, cookie replaced…), drop its open
      // tabs / recents before the editor mounts and restores them.
      await claimBrowserForUser(me.user_id);
      setUser(me);
      return me;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  // A 401 from a data endpoint while signed in = the session ended (expired / signed out elsewhere).
  const userRef = useRef(user);
  userRef.current = user;
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!userRef.current) return;
      setUser(null);
      setExpiredNotice(true);
      forgetLocalAccountState();
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // A 403 subscription_required while signed in = the plan lapsed (trial over, card failed…): re-read the account so
  // `editor_access` turns false and the route gate moves the user to /subscribe. Debounced — a burst of failing
  // requests (autosave, library, fonts) must not hammer /auth/me.
  const gateRefreshAt = useRef(0);
  useEffect(() => {
    setSubscriptionRequiredHandler(() => {
      if (!userRef.current || userRef.current.editor_access === false) return;
      const now = Date.now();
      if (now - gateRefreshAt.current < 5000) return;
      gateRefreshAt.current = now;
      refresh();
    });
    return () => setSubscriptionRequiredHandler(null);
  }, [refresh]);

  const signOut = useCallback(async () => {
    setUser(null); // RequireAuth leaves the editor at once, so no pending session write can resurrect the account's tabs
    resetLibraryIndex(); // folder colours on the document tabs belong to this account only
    try { await apiLogout(); } catch { /* the cookie is cleared server-side; a network hiccup should not keep the user signed in locally */ }
    await forgetLocalAccountState();
  }, []);

  /** Permanently delete the account (the backend wipes everything it owns), then forget this browser's local state. */
  const deleteAccount = useCallback(async (confirmation) => {
    await apiDeleteAccount(confirmation); // throws (401 wrong password / 422) before anything is removed
    setUser(null);
    resetLibraryIndex();
    setExpiredNotice('deleted');
    await forgetLocalAccountState();
  }, []);

  const value = useMemo(() => ({ user, loading, refresh, setUser, signOut, deleteAccount, expiredNotice, clearExpiredNotice: () => setExpiredNotice(false) }), [user, loading, refresh, signOut, deleteAccount, expiredNotice]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
